// The demo story. Language: Dutch as spoken in Flanders. Setting: thriller ("Kanaal").
// Structure follows spelverloop.md: linear scenario, choices only colour the reactions,
// personal details (time of day, name, earlier answers) create the illusion of a personal story.

import { api, type NameResult } from './api.js';
import { sfx } from './audio.js';
import { backstage } from './backstage.js';
import { Chat } from './chat.js';
import { runLock } from './tasks/lock.js';
import { runOffline } from './tasks/offline.js';
import { runSign } from './tasks/sign.js';
import { askToInstall } from './install.js';
import { clearBadge, deliveryResult, explainFailure, onPushOpen, permission, pushSupport, schedule, subscribe } from './push.js';
import { openSheet } from './sheet.js';
import { $, daysAgo, el, escapeHtml, longDate, pick, sleep, wait } from './util.js';

export interface StoryState {
  session?: string;
  startedAt: number;
  firstName: string;
  nameConfidence: number;
  nameAttempts: number;
  tasks: string[];
  /** How Joris can reach the player at the end of the demo. */
  reach?: 'push' | 'push-manual' | 'declined' | 'needs-install' | 'unsupported';
}

const STATE_KEY = 'vl-state';
const PUSH_SERVICES: Record<string, string> = { 'web.push.apple.com': 'Apple', 'fcm.googleapis.com': 'Google (FCM)', 'updates.push.services.mozilla.com': 'Mozilla' };
const serviceName = (host?: string) => (host ? PUSH_SERVICES[host] ?? host : 'onbekend');

const DOOR_YEAR = '1898';

/** Joris reacts to the reason the name model gives, never with a generic error. */
const NAME_REPLIES: Record<string, (t: string, r: NameResult) => string[]> = {
  gibberish: () => ['Dat is geen naam. Dat is je toetsenbord.', 'Ik kan dat niet lezen. Nog eens?', 'Trillen je handen? Je naam.'],
  word: (t) => [`“${t}”? Dat is geen naam.`, 'Ik vroeg je naam. Niet het eerste woord dat je zag.', 'Hoe noemen je vrienden je?'],
  fictional: (_t, r) => [`Ha. ${r.name ?? ''}. Ik heb geen tijd voor grappen.`, 'Grappig. En nu je échte naam.'],
  offensive: () => ['Laat maar. Ik doe alsof ik dat niet gelezen heb.', 'Ik heb al genoeg aan mijn hoofd. Je naam.'],
  refusal: () => ['Ik snap dat je voorzichtig bent. Een voornaam volstaat.', 'Je hoeft me niets anders te vertellen. Alleen hoe ik je mag noemen.'],
  question: () => ['Ik vroeg het eerst.', 'Eerst jij. Hoe heet je?'],
  sentence: () => ['Korter. Alleen je naam.', 'Gewoon je voornaam is goed.'],
  too_short: () => ['Eén letter? Iets meer dan dat.', 'Dat is geen naam. Dat is een begin.'],
  digits: () => ['Cijfers? Je bent toch geen code.', 'Zonder cijfers. Hoe heet je?'],
  empty: () => ['…?'],
  character_ellen: () => ['Nee. Jij bent Ellen niet. Ellen zou mij nooit zo antwoorden.', 'Dat is háár naam. Niet de jouwe.'],
};

const REACH_TEXT: Record<NonNullable<StoryState['reach']>, string> = {
  push: 'Je sloot de app, Joris stuurde een melding, en één tik bracht je terug in het verhaal.',
  'push-manual': 'Joris stuurde een melding terwijl de app dicht was. Een tik erop brengt je meteen terug in het verhaal.',
  declined: 'Met meldingen kan Joris je terug in het verhaal halen, ook als de app dicht is. Je koos er nu voor van niet.',
  'needs-install': 'Op het beginscherm kan Joris je een melding sturen, ook als de app dicht is. Zet Kanaal op je beginscherm en speel opnieuw.',
  unsupported: 'Op toestellen die het ondersteunen, stuurt Joris een melding die je terug in het verhaal brengt, ook als de app dicht is.',
};

export class Story {
  readonly chat = new Chat();
  state: StoryState = { startedAt: Date.now(), firstName: '', nameConfidence: 0, nameAttempts: 0, tasks: [] };
  private used = new Set<string>();
  private screen = $('#screen');

  private say = (text: string, opts?: Parameters<Chat['say']>[2]) => this.chat.say('joris', text, opts);

  private async glitch(ms = 700) {
    sfx.glitch();
    this.screen.classList.add('is-glitching');
    await sleep(ms);
    this.screen.classList.remove('is-glitching');
  }

  async run() {
    const n = () => this.state.firstName;
    const chat = this.chat;
    chat.reset();

    // --- Ellen's history: the player reads someone else's channel --------------------------
    const lost = daysAgo(21);
    chat.day(longDate(lost));
    await wait(500);
    await chat.say('joris', 'Waar zit je? Mama vraagt naar je.', { history: true, time: '19:02' });
    await chat.say('ellen', 'Bezig. Ik bel je morgen.', { history: true, time: '19:40' });
    await chat.say('joris', 'Ellen. Wat is er aan de hand?', { history: true, time: '21:15' });
    await chat.say('ellen', 'Ik heb iets gevonden. In het stadsarchief. Een adres dat niet zou mogen bestaan.', { history: true, time: '22:31' });
    chat.photo('ellen', '/assets/ellen-street.jpg', '22:47', { history: true, time: '22:47' });
    await wait(300);
    await chat.say('ellen', 'Ik ga er vanavond kijken. Als ik me niet meld, bel dan niet de politie.', { history: true, time: '22:48' });
    await chat.say('joris', 'Waarom niet??', { history: true, time: '22:49' });
    await chat.say('joris', 'Ellen?', { history: true, time: '23:30' });
    await chat.say('joris', 'Ellen, antwoord.', { history: true, time: '07:12' });
    chat.day(longDate(daysAgo(14)));
    chat.deleted('ellen', { history: true, time: '03:03' });
    backstage.log('verhaal', 'Opening: de gevonden telefoon', 'De speler leest de berichten van iemand anders (opening 2 uit <code>openingen.md</code>). Datums worden berekend vanaf vandaag: “21 dagen geleden” klopt altijd.');
    backstage.log('illusie', 'Verwijderd bericht', 'Iemand anders had toegang tot dit kanaal. Kost niets om te bouwen, zet meteen spanning.');

    // --- Live: someone writes back ------------------------------------------------------------
    await wait(1800);
    chat.day('Vandaag');
    chat.system('Ellen V. is online');
    await wait(1600);
    await this.say('Ellen??', { pause: 300 });
    await this.say('Ben jij dat? Je bent online.');
    const first = await chat.choose(['Ik ben Ellen niet.', 'Wie ben jij?'], { quiet: 'Niets zeggen', timeoutMs: 25000 });
    if (first === 'Ik ben Ellen niet.') {
      await this.say('Nee. Dat dacht ik al.');
      await this.say('Ellen begint nooit met een hoofdletter.');
    } else if (first === 'Wie ben jij?') {
      await this.say('Haar broer. Joris.');
      await this.say('Maar ik vroeg het eerst. Jij bent Ellen niet, hè?');
    } else {
      await this.say('Ik zie dat je meeleest.', { pause: 900 });
      await this.say('Jij bent Ellen niet.');
    }
    backstage.log('illusie', 'Keuze zonder vertakking', `Gekozen: <code>${escapeHtml(first ?? 'niets (wachten)')}</code>. Elke keuze krijgt een eigen reactie, daarna loopt het verhaal voor iedereen gelijk verder (<code>spelverloop.md</code> §4). Ook zwijgen is een keuze.`);

    // Time of day on the player's device: a small, uncanny detail without asking anything
    const h = new Date().getHours();
    const timeLine = h >= 22 || h < 5 ? 'Het is al laat bij jou. Bij mij ook. Ik slaap niet veel sinds ze weg is.'
      : h < 12 ? 'Zo vroeg al wakker. Ik ben nog niet gaan slapen.'
      : h < 18 ? 'Midden op de dag, en jij leest het kanaal van mijn zus.'
      : 'Het wordt donker. Dan kwam zij altijd online.';
    await this.say(timeLine);
    backstage.log('illusie', 'Tijdstip van het toestel', `Lokale tijd ${String(h).padStart(2, '0')}u → Joris reageert daarop. Er is nog geen enkel gegeven gevraagd.`);

    // --- The name, validated by the model ------------------------------------------------------
    await this.say('Hoe heet je?');
    await this.askName();
    await this.say(`${n()}.`, { pause: 700 });
    await this.say(`Oké, ${n()}. Ik ben Joris. Ellen is mijn zus.`);
    await this.say('Ze is drie weken weg. Haar telefoon is nooit gevonden. Dit kanaal ook niet.');
    await this.say('Tot jij het daarnet opende.');
    const how = await chat.choose(['Het stond gewoon open.', 'Hoe kan dat?']);
    await this.say(how === 'Hoe kan dat?' ? 'Ze zei altijd: het kanaal kiest zelf wie het hoort.' : 'Dat zei ze ook altijd. “Het kanaal kiest zelf wie het hoort.”');
    await this.say('Ellen bewaarde belangrijke dingen niet in gewone berichten. Ze verstopte ze. Mij laat het kanaal niet binnen.');
    await this.say('Maar jou misschien wel.');

    // --- Someone else is listening ---------------------------------------------------------------
    await wait(900);
    this.screen.classList.add('phase-2');
    await this.glitch();
    chat.system('Een onbekend apparaat heeft zich verbonden', 'alarm');
    await chat.say('unknown', `Sluit dit kanaal, ${n()}.`, { pause: 600 });
    await this.glitch(400);
    backstage.log('illusie', 'Een onbekende kent je naam', 'Gewoon de variabele <code>{naam}</code> die net gevalideerd werd. Een nieuw personage zonder uitleg (<code>spelverloop.md</code> §6), en de interface wordt een tint donkerder.');
    await this.say('Wie was dat??', { pause: 300 });
    await this.say('Hoe weet die hoe je heet?');
    await this.say('Ze lezen mee. Ellen zei het: als ze meelezen, verbreek de verbinding.');
    await this.say('Doe het. Ik wacht hier.');

    // --- Task 1: disconnect ------------------------------------------------------------------------
    const t1 = await runOffline();
    this.state.tasks.push('verbinding');
    chat.taskCard(1, 'Verbreek de verbinding', t1.method === 'offline' ? 'netwerk uit · bericht gevonden' : 'duim op het scherm · bericht gevonden');
    chat.photo('ellen', '/assets/door.jpg', 'verborgen bericht · bewaard in je kanaal', { variant: 'hidden', time: '02:14' });
    await this.say(t1.method === 'offline' ? `${n()}? Je was even helemaal weg.` : `${n()}? Wat zag je?`, { pause: 900 });
    await this.say('Ik zie hier alleen een foto van een deur. Stond er iets boven die deur?');
    await this.askYear();

    // --- Task 2: the lock ----------------------------------------------------------------------------
    await this.say('In het kanaal staat nog een map van haar. Op slot. Vier cijfers.');
    await this.say('Ik heb alles geprobeerd. Haar verjaardag. De mijne. Mama’s.');
    await this.say('Probeer jij het. Ik denk dat jij de code al kent.');
    const t2 = await runLock(DOOR_YEAR);
    this.state.tasks.push('slot');
    chat.taskCard(2, 'Het slot', `code ${DOOR_YEAR} · ${t2.attempts} poging${t2.attempts === 1 ? '' : 'en'}`);
    chat.photo('ellen', '/assets/symbol.jpg', `map “${DOOR_YEAR}”`, { variant: 'hidden' });
    await this.say('Dat teken…', { pause: 900 });
    await this.say('Dat stond ook op haar kamerdeur. Met krijt. Ik dacht dat het van een kind was.');
    const why = await chat.choose(['Wat betekent het?', 'Waar staat het nog?']);
    await this.say(why === 'Wat betekent het?' ? 'Geen idee. Maar ze is het gaan zoeken.' : 'Overal waar zij kwam, blijkbaar. En ze is het gaan zoeken.');

    this.screen.classList.add('phase-3');
    await this.glitch(500);
    await chat.say('unknown', `Teken het niet, ${n()}.`, { pause: 300 });
    await this.say('Negeer dat.', { pause: 400 });
    await this.say('Haar laatste bericht is vergrendeld. Met dat teken.');

    // --- Task 3: draw the sign ------------------------------------------------------------------------
    const t3 = await runSign();
    this.state.tasks.push('teken');
    chat.taskCard(3, 'Teken het teken', t3.method === 'drawn' ? `getekend · ${Math.round(t3.score * 100)}% herkend${t3.dot ? ' · met stip' : ''}` : 'gekozen');

    // --- Ending: the cliffhanger ------------------------------------------------------------------------
    sfx.startDrone();
    chat.system('Laatste bericht van Ellen ontgrendeld', 'ok');
    await wait(1400);
    chat.day(longDate(lost));
    const slow = { history: true, time: '23:58' } as const;
    await wait(1200); await chat.say('ellen', `Voor ${n()}.`, slow);
    await wait(2200); await chat.say('ellen', 'Als je dit leest, heeft het kanaal jou gekozen. Joris mag dit niet zien.', slow);
    await wait(2600); await chat.say('ellen', 'Het begint niet in het archief. Het begint bij jou in de buurt.', slow);
    await wait(2600); await chat.say('ellen', 'Er staat een teken in jouw straat. Zoek het. Dan vind je mij.', slow);
    backstage.log('illusie', 'Een bericht van drie weken geleden, met jouw naam', 'Het verhaal is lineair en voor iedereen hetzelfde. Wat het persoonlijk maakt, zijn de details: je naam, je tijdstip, straks je eigen straat.');
    await wait(2400);
    await this.glitch(500);
    await this.say('…', { pause: 600 });
    await this.say('Wat stond er? Ik zie alleen een leeg bericht.');
    await this.say(`Ellen is drie weken weg, ${n()}. Hoe kan zij jouw naam kennen?`);
    const last = await chat.choose(['Ik weet het niet.', 'Ze schrijft dat het teken in mijn straat staat.']);
    if (last === 'Ik weet het niet.') await this.say('Ik ook niet. Maar dit is geen toeval.');
    else await this.say('In jouw straat? Waar woon je?');
    await this.say('Als dit kanaal wegvalt, verlies ik je.');
    sfx.stopDrone();
    await this.reachStep();
    await wait(1200);
    this.showEnd();
  }

  /** Saved so the story can continue when a notification cold-starts the app. */
  private save(stage: string) {
    try { localStorage.setItem(STATE_KEY, JSON.stringify({ ...this.state, stage, savedAt: Date.now() })); } catch { /* private mode */ }
  }

  // --- Push: can Joris bring the player back? -------------------------------------------------------
  private async reachStep() {
    const n = this.state.firstName;
    const support = pushSupport();
    if (support !== 'ok' || !this.state.session) {
      this.state.reach = support === 'needs-install' ? 'needs-install' : 'unsupported';
      backstage.log('sensor', 'Pushmeldingen niet mogelijk', support === 'needs-install'
        ? 'iOS: pushmeldingen werken enkel voor een webapp op het beginscherm (iOS 16.4+). Het personage vraagt om het kanaal daar te bewaren.'
        : support === 'insecure' ? 'Geen HTTPS: service worker en push zijn niet beschikbaar. Gebruik de tunnel-URL.' : 'Deze browser ondersteunt geen Web Push.');
      await this.say('Maar hier kan ik je niet bereiken. Niet zolang het kanaal in een gewone browser openstaat.');
      if (support === 'needs-install') {
        await this.say('Bewaar het kanaal op je beginscherm. Dan vind ik je wel.');
        await askToInstall({ label: 'Buiten het verhaal', title: 'Zet Kanaal op je beginscherm', quote: 'Op een iPhone kan Joris je alleen een melding sturen als het spel op je beginscherm staat.', continueLabel: 'Afronden' });
      }
      return;
    }

    await this.say('Laat me je een seintje geven als ik iets vind. Alleen dan.');
    const sheet = openSheet({ label: 'Laatste stap', title: 'Laat Joris je bereiken', quote: '“Ik stuur je een bericht. Ook als het kanaal dicht is.”' });
    const allow = el('button.btn.btn--primary', { type: 'button' }, permission() === 'granted' ? 'Verder' : 'Meldingen toestaan');
    const skip = el('button.btn.btn--ghost', { type: 'button' }, 'Liever niet');
    sheet.body.append(
      el('p.sheet__note', {}, 'Buiten het verhaal: je toestel vraagt zo om meldingen toe te staan. In deze demo stuurt Joris er precies één, om te testen of hij je terug in het spel kan halen.'),
      el('div.sheet__actions', {}, allow, skip));
    const choice = await new Promise<'allow' | 'skip'>((r) => {
      allow.addEventListener('click', () => r('allow'), { once: true });
      skip.addEventListener('click', () => r('skip'), { once: true });
    });
    const sub = choice === 'allow' ? await subscribe(this.state.session) : { ok: false, reason: 'dismissed' as const };
    if (!sub.ok) {
      await sheet.close();
      this.state.reach = 'declined';
      backstage.log('privacy', 'Meldingen niet toegestaan', `Resultaat: <code>${sub.reason}</code>. Het verhaal loopt gewoon verder; in het spel blijft e-mail als kanaal over.`);
      await this.say(sub.reason === 'error' ? 'Het lukt niet. De verbinding is te zwak.' : 'Dan niet. Ik vind je wel.');
      return;
    }
    backstage.log('sensor', 'Pushabonnement aangemaakt', `Toestemming gegeven, abonnement bij <code>${escapeHtml(serviceName(sub.service))}</code> in ${sub.ms} ms. Onze server bewaart enkel dat adres en stuurt met eigen VAPID-sleutels: geen externe meldingendienst.`);

    // The test: close the app, receive the push, tap it, come back.
    const status = el('p.sheet__feedback', {}, 'Wachten tot je de app sluit…');
    const now = el('button.btn.btn--ghost.btn--small', { type: 'button' }, 'Stuur de melding nu (app open laten)');
    sheet.body.replaceChildren(
      el('ol.steps', {},
        el('li', {}, el('span.steps__icon', {}, '1'), el('span', { html: '<b>Sluit de app</b>: ga naar je beginscherm of vergrendel je scherm.' })),
        el('li', {}, el('span.steps__icon', {}, '2'), el('span', { html: 'Joris stuurt binnen enkele seconden een <b>melding</b>.' })),
        el('li', {}, el('span.steps__icon', {}, '3'), el('span', { html: '<b>Tik op de melding</b> om terug te keren.' }))),
      status, el('div.sheet__actions', {}, now));
    await this.say('Goed. Sluit het kanaal nu maar.', { pause: 300 });

    let scheduledAt = 0;
    const send = async (delay: number, how: string) => {
      if (scheduledAt) return;
      scheduledAt = Date.now() + delay * 1000;
      this.save('awaiting-push');
      const since = Date.now();
      const ok = await schedule(this.state.session!, 'story', delay, n);
      status.textContent = ok ? 'Melding onderweg…' : 'De melding kon niet verstuurd worden.';
      backstage.log('ai', 'Melding gepland', `${how}: de server stuurt over ${delay} s een pushbericht van “Joris”.`);
      if (!ok) return;
      const r = await deliveryResult(this.state.session!, since, (delay + 20) * 1000);
      if (r?.ok) backstage.log('sensor', 'Melding afgeleverd', `${escapeHtml(serviceName(r.service))} aanvaardde het bericht (HTTP ${r.status}). Afzender (VAPID subject): <code>${escapeHtml(r.subject)}</code>.`);
      else if (r) {
        status.textContent = 'De melding werd geweigerd. Ga verder met de knop hieronder.';
        status.className = 'sheet__feedback is-bad';
        backstage.log('sensor', 'Melding geweigerd', `${escapeHtml(serviceName(r.service))}: <code>${escapeHtml(explainFailure(r))}</code>. Afzender (VAPID subject): <code>${escapeHtml(r.subject)}</code>.`);
      }
    };
    now.addEventListener('click', () => void send(3, 'Knop'));

    const result = await new Promise<{ via: 'push' | 'manual'; at: number }>((resolve) => {
      let done = false;
      const finish = (via: 'push' | 'manual') => {
        if (done) return; done = true;
        stop(); document.removeEventListener('visibilitychange', onVis);
        resolve({ via, at: Date.now() });
      };
      const stop = onPushOpen(() => finish('push'));
      const onVis = () => {
        if (document.hidden) { void send(5, 'App gesloten (Page Visibility)'); return; }
        // Back without tapping the notification? Give the service worker a moment to report a tap first.
        if (scheduledAt) setTimeout(() => { if (!done && Date.now() > scheduledAt) finish('manual'); }, 2500);
      };
      document.addEventListener('visibilitychange', onVis);
      // App left open: after the message arrived, offer a way to continue
      const check = setInterval(() => {
        if (done) return clearInterval(check);
        if (scheduledAt && Date.now() > scheduledAt + 8000 && !document.hidden) {
          clearInterval(check);
          const cont = el('button.btn.btn--primary.btn--small', { type: 'button' }, 'Ik heb de melding gezien');
          cont.addEventListener('click', () => finish('manual'));
          now.replaceWith(cont);
        }
      }, 1000);
    });

    clearBadge();
    try { localStorage.removeItem(STATE_KEY); } catch { /* ignore */ }
    await sheet.close();
    this.state.reach = result.via === 'push' ? 'push' : 'push-manual';
    const secs = scheduledAt ? Math.max(0, Math.round((result.at - scheduledAt) / 1000)) : 0;
    backstage.log('sensor', result.via === 'push' ? 'Teruggehaald via de melding' : 'Zelf teruggekeerd', result.via === 'push'
      ? `Tik op de melding → de service worker brengt de app naar voren (<code>clients.focus</code>, warme start) en meldt het aan de pagina. ${secs} s na verzenden.`
      : 'De speler kwam terug zonder op de melding te tikken. De melding zelf werkte wel.');
    await this.say(result.via === 'push' ? 'Je bent terug.' : 'Je bent terug. Zag je mijn bericht?', { pause: 600 });
    await this.say(`Zie je, ${n}? Ook als het kanaal dicht is, kan ik je vinden.`);
  }

  /** Cold start: the app was closed and a tap on the notification opened it again. */
  async resumeFromPush(sentAt: number | null) {
    let saved: Partial<StoryState> & { stage?: string } = {};
    try { saved = JSON.parse(localStorage.getItem(STATE_KEY) ?? '{}'); } catch { /* ignore */ }
    try { localStorage.removeItem(STATE_KEY); } catch { /* ignore */ }
    Object.assign(this.state, saved, { reach: 'push' });
    const n = this.state.firstName;
    clearBadge();
    const secs = sentAt ? Math.max(0, Math.round((Date.now() - sentAt) / 1000)) : null;
    backstage.log('sensor', 'Teruggehaald via de melding (koude start)', `De app was helemaal gesloten. Tik op de melding → <code>clients.openWindow</code> opent het spel opnieuw${secs !== null ? `, ${secs} s na verzenden` : ''}. Het verhaal gaat verder dankzij de bewaarde toestand${saved.stage ? '' : ' (niet gevonden: we gaan verder zonder naam)'}.`);
    this.chat.reset();
    this.screen.classList.add('phase-2');
    this.chat.setStatus('online', 'live');
    this.chat.day('Vandaag');
    this.chat.system('Kanaal heropend via een melding', 'ok');
    await this.say(n ? `${n}. Je bent terug.` : 'Je bent terug.', { pause: 900 });
    await this.say('Zie je? Ook als het kanaal dicht is, kan ik je vinden.');
    await wait(1200);
    this.showEnd();
  }

  private async askName() {
    for (let attempt = 1; ; attempt++) {
      this.state.nameAttempts = attempt;
      const text = await this.chat.ask('Je voornaam…', { autocomplete: 'given-name', maxlength: 60 });
      let r: NameResult;
      try {
        r = await api.name(text, attempt, this.state.session);
      } catch {
        // No server: accept what was typed rather than block the story
        const first = text.split(/\s+/)[0].slice(0, 30);
        r = { verdict: 'accept', reason: 'name', name: first, firstName: first, confidence: 0, stage: 'rules', rule: 'server unreachable', tokens: [], ms: 0 };
      }
      backstage.log('ai', `Naammodel: “${text.slice(0, 40)}”`, '', backstage.nameResult(r));

      if (r.verdict === 'accept') return this.setName(r);
      if (r.verdict === 'confirm') {
        await this.say(r.reason === 'character_joris' ? `${r.firstName}? Zo heet ik ook.` : `${r.firstName}?`);
        await this.say(r.reason === 'character_joris' ? 'Is dat echt je naam, of speel je met me?' : 'Is dat echt je naam?');
        const yes = await this.chat.choose(['Ja, echt.', 'Nee, wacht.']);
        if (yes === 'Ja, echt.') {
          backstage.log('ai', 'Bij twijfel: navragen', 'Het model was niet zeker. Liever het personage laten navragen dan een echte naam afwijzen (<code>ai-model.md</code> §3).');
          return this.setName(r);
        }
        await this.say('Zeg het dan.');
        continue;
      }
      if (attempt >= 5) {
        await this.say('Laat maar. Ik noem je gewoon Vreemde.');
        this.state.firstName = 'Vreemde';
        return;
      }
      const options = (NAME_REPLIES[r.reason] ?? NAME_REPLIES.gibberish)(text.slice(0, 30), r);
      await this.say(pick(options, this.used));
    }
  }

  private setName(r: NameResult) {
    this.state.firstName = (r.firstName ?? r.name ?? 'Vreemde').slice(0, 30);
    this.state.nameConfidence = r.confidence;
  }

  private async askYear() {
    for (let attempt = 1; attempt <= 4; attempt++) {
      const text = await this.chat.ask('Wat stond er?', { maxlength: 80 });
      let outcome: 'correct' | 'incorrect' | 'unclear' = 'unclear';
      let extracted: string | null = null;
      try {
        const r = await api.answer(text, DOOR_YEAR);
        outcome = r.outcome; extracted = r.extracted;
        backstage.log('ai', `Antwoord beoordeeld: ${r.outcome}`, `“${escapeHtml(text.slice(0, 50))}” → ${r.method === 'number-words' ? 'getal in woorden herkend' : r.method === 'digits' ? 'cijfers' : 'geen getal'}: <code>${escapeHtml(r.rule.replace('found', 'gevonden').replace('expected', 'verwacht').replace('no number found', 'geen getal'))}</code>. Regels eerst; een taalmodel is hier niet nodig.`);
      } catch { if (text.includes(DOOR_YEAR)) outcome = 'correct'; }

      if (outcome === 'correct') {
        await this.say(`${DOOR_YEAR}.`);
        await this.say('Het jaar dat het stadsarchief gebouwd werd. Daar werkte ze.');
        return;
      }
      if (attempt === 3) {
        await this.say(`Wacht. Ik zie het nu ook. ${DOOR_YEAR}, toch?`);
        await this.chat.choose(['Ja.', 'Ik denk het.']);
        await this.say('Het jaar dat het stadsarchief gebouwd werd. Daar werkte ze.');
        return;
      }
      if (extracted === '17') await this.say('Dat is het huisnummer. Ik bedoel in de steen, boven de deur.');
      else if (outcome === 'incorrect') await this.say(`${extracted}? Kijk nog eens naar de foto. Tik erop om ze groter te zien.`);
      else await this.say(pick(['Een jaartal, denk ik. In de steen.', 'Er staan cijfers in de steen boven de deur.'], this.used));
    }
  }

  private showEnd() {
    const mins = Math.max(1, Math.round((Date.now() - this.state.startedAt) / 60000));
    const card = el('section.endcard', { 'aria-label': 'Einde van de demo' },
      el('p.endcard__label', {}, 'Einde van de demo'),
      el('h2', {}, 'Hier begint het echte spel.'),
      el('p', {}, 'In de volledige versie vraagt Joris nu je e-mailadres, met een code van zes cijfers en zonder wachtwoord, en ongeveer waar je woont. Het teken staat dan op een echte, openbare plek bij jou in de buurt.'),
      el('div.endcard__stats', {},
        el('div', {}, el('strong', {}, `${mins}′`), el('span', {}, 'gespeeld')),
        el('div', {}, el('strong', {}, `${this.state.tasks.length}/3`), el('span', {}, 'opdrachten')),
        el('div', {}, el('strong', {}, this.state.nameConfidence ? `${Math.round(this.state.nameConfidence * 100)}%` : '–'), el('span', {}, 'naam herkend'))),
      el('ul', {},
        el('li', {}, el('b', {}, 'Een opening die zonder gegevens werkt'), 'De speler doet meteen iets. Pas daarna vraagt een personage zijn naam, in het verhaal.'),
        el('li', {}, el('b', {}, 'Een licht AI-model op onze eigen server'), 'Herkent namen, gebrabbel, grapjes en weigeringen, in milliseconden en zonder externe AI-dienst.'),
        el('li', {}, el('b', {}, 'Drie opdrachten met de echte telefoon'), 'Netwerk, aanraking of beweging, en tekenen. Elke opdracht heeft een alternatief.'),
        el('li', {}, el('b', {}, 'De illusie van een persoonlijk verhaal'), 'Eén lineair verhaal, persoonlijke details: tijdstip, naam, eerdere antwoorden. Straks ook je eigen buurt.'),
        el('li', {}, el('b', {}, 'Een spel dat jou terugvindt'), REACH_TEXT[this.state.reach ?? 'unsupported'])),
    );
    const again = el('button.btn.btn--primary', { type: 'button' }, 'Opnieuw spelen');
    const behind = el('button.btn.btn--ghost', { type: 'button' }, 'Achter de schermen');
    again.addEventListener('click', () => location.reload());
    behind.addEventListener('click', () => document.dispatchEvent(new CustomEvent('backstage:open')));
    card.append(again, behind);
    this.screen.append(card);
    backstage.log('verhaal', 'Einde van de demo', `Gespeeld in ${mins} min. In het spel volgen nu e-mail (code van 6 cijfers) en de thuislocatie, benaderd op ~100–200 m (<code>onboarding.md</code>).`);
  }
}
